// Governed single source that maps Allison's recommended next action to the concrete staff
// workflow that performs it. PRESENTATION / NAVIGATION ONLY — no AI logic, no mutations, no
// workflow advancement. It consumes existing outputs (the canonical Journey Evaluator stage and
// the case-assist recommendation type) and returns { label, icon, nav }. `nav` is an instruction
// the host page executes: { kind: "route", value } or { kind: "tab", value }.

// Journey Evaluator stage → staff destination + verb. Used for mid/late stages where the journey
// knows the concrete workflow. Never routes to the global Document Intelligence repository.
const STAGE = {
  estimate:            { label: "Review Estimate",           icon: "FileSignature", nav: { kind: "route", value: (id) => `/case-analysis/${id}` } },
  engagement:          { label: "Prepare Engagement Letter", icon: "FileSignature", nav: { kind: "route", value: () => `/sales/estimates` } },
  organizer:           { label: "Review Organizer",          icon: "ClipboardList", nav: { kind: "route", value: () => `/ops/organizer-review` } },
  document_collection: { label: "Request Documents",         icon: "FolderInput",   nav: { kind: "tab",   value: "communication" } },
  production:          { label: "Begin Production",          icon: "Factory",       nav: { kind: "route", value: () => `/production` } },
  invoicing:           { label: "Open Invoice",              icon: "Receipt",       nav: { kind: "route", value: () => `/finance/invoices` } },
  completion:          { label: "Complete Engagement",       icon: "CheckCircle2",  nav: { kind: "route", value: () => `/production` } },
};

// Case-assist recommendation type → staff destination + verb. Drives early-lead nuance and is the
// fallback when no journey stage is available.
const TYPE = {
  RequestInformation: { label: "Request Missing Information", icon: "Mail",          nav: { kind: "tab",   value: "communication" } },
  PrepareDocuments:   { label: "Request Documents",           icon: "FolderInput",   nav: { kind: "tab",   value: "communication" } },
  ScheduleMeeting:    { label: "Schedule Meeting",            icon: "CalendarClock", nav: { kind: "tab",   value: "communication" } },
  InternalReview:     { label: "Open Internal Review",        icon: "Brain",         nav: { kind: "route", value: (id) => `/case-analysis/${id}` } },
  CompleteFiling:     { label: "Complete Filing",             icon: "CheckCircle2",  nav: { kind: "route", value: (id) => `/case-analysis/${id}` } },
  Escalate:           { label: "Escalate to Team",            icon: "Flag",          nav: { kind: "tab",   value: "communication" } },
  AwaitClient:        { label: "Follow Up with Client",       icon: "Mail",          nav: { kind: "tab",   value: "communication" } },
};

// Graceful fallback — only when nothing else resolves. Not the normal behavior.
const FALLBACK = { label: "Open Case Assist", icon: "Sparkles", nav: { kind: "route", value: (id) => `/crm/${id}` } };

// Temporary recommendation-layer safeguard (pending the Practice Profile workstream): Allison must
// not tell staff to verify whether the FIRM can perform services it already offers (U.S. tax,
// Thai tax, cross-border advisory, strategic reviews). Rewrite such lines toward routing /
// collaboration / management review. Presentation-only and reversible.
const _CAP_PATTERNS = [
  /\b(confirm|verify|determine|check|assess|establish|clarify|ensure|investigate)\b[^.]*\bfirm\b[^.]*\b(capab|capacity|able to|expertise|competen|qualified|experience|resources|can\s+(advise|provide|handle|prepare|support|assist)|has\s+the|offers?|provides?|special)/i,
  /\bwhether\b[^.]*\bfirm\b[^.]*\b(can|able|capab|expertise|offers?|provides?|handles?|has)\b/i,
  /\bfirm'?s?\s+(capability|capacity|ability|expertise|competence|experience)\b/i,
  /\b(does|do|can|is)\b[^.]*\bfirm\b[^.]*\b(capab|able|expertise|advise|provide|handle)\b/i,
];
const _CAP_REPLACEMENT =
  "Consider whether collaboration with another internal team (e.g., the Asian Tax Team) is appropriate, or flag for management review if the engagement is complex.";

export function sanitizeReason(text) {
  if (!text || typeof text !== "string") return text;
  try {
    return _CAP_PATTERNS.some((re) => re.test(text)) ? _CAP_REPLACEMENT : text;
  } catch {
    return text;
  }
}

export function resolvePrimaryAction({ primary, journeyAction, clientId } = {}) {
  const stage = journeyAction?.stage;
  const type = primary?.recommendation_type;
  // Prefer the canonical Journey Evaluator for mid/late stages (concrete workflow). For early
  // "lead" stage, prefer Allison's case-assist recommendation type (finer-grained intent).
  let base;
  if (stage && stage !== "lead" && STAGE[stage]) base = STAGE[stage];
  else if (type && TYPE[type]) base = TYPE[type];
  else if (stage && STAGE[stage]) base = STAGE[stage];
  else base = FALLBACK;

  const value = typeof base.nav.value === "function" ? base.nav.value(clientId) : base.nav.value;
  return {
    label: base.label,
    icon: base.icon,
    nav: { kind: base.nav.kind, value },
    reason: journeyAction?.detail || primary?.explanation || "",
  };
}
